import React, { useState, useEffect } from 'react';
import useSessionStorage from '../hooks/useSessionStorage';
import { Plus, Edit2, Trash2, Download } from 'lucide-react';
import Modal from '../components/Modal';
import { expensesService } from '../services/supabaseService';
import { isPersonalCategory } from '../utils/expenseCategories';
import { 
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip, ResponsiveContainer, Cell,
  PieChart, Pie
} from 'recharts';

const parseDateSafe = (dateStr) => {
  if (!dateStr) return null;
  if (dateStr instanceof Date) return dateStr;
  
  const datePart = typeof dateStr === 'string' ? dateStr.split('T')[0].split(' ')[0] : '';
  const parts = datePart.split('-');
  if (parts.length === 3) {
    const year = parseInt(parts[0], 10);
    const month = parseInt(parts[1], 10) - 1;
    const day = parseInt(parts[2], 10);
    return new Date(year, month, day);
  }
  return new Date(dateStr);
};

const formatDateLocal = (isoStr) => {
  if (!isoStr) return '';
  const d = parseDateSafe(isoStr);
  if (!d) return '';
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

const getMonthShortName = (isoStr) => {
  if (!isoStr) return '';
  const d = parseDateSafe(isoStr);
  return d ? d.toLocaleString('default', { month: 'short' }) : '';
};

const Expenses = () => {
  const [expenses, setExpenses] = useState([]);
  const [isModalOpen, setIsModalOpen] = useSessionStorage('expenses_isModalOpen', false);
  const [editingExpense, setEditingExpense] = useSessionStorage('expenses_editingExpense', null);
  const [filterCategory, setFilterCategory] = useSessionStorage('expenses_filterCategory', 'All');
  const [filterSubcategory, setFilterSubcategory] = useSessionStorage('expenses_filterSubcategory', 'All');
  const [startDate, setStartDate] = useSessionStorage('expenses_startDate', '');
  const [endDate, setEndDate] = useSessionStorage('expenses_endDate', '');
  const [filterMonth, setFilterMonth] = useSessionStorage('expenses_filterMonth', '');
  
  // Dynamic Category Map
  const [categoryMap, setCategoryMap] = useState({});

  // Form State
  const [formData, setFormData] = useSessionStorage('expenses_formData', {
    description: '', amount: '', category: 'Utilities', subcategory: 'Electricity', quantity: 1, metric: 'units', expense_date: ''
  });

  // Custom Category/Subcategory States
  const [isAddingCategory, setIsAddingCategory] = useState(false);
  const [newCategoryName, setNewCategoryName] = useState('');
  const [isAddingSubcategory, setIsAddingSubcategory] = useState(false);
  const [newSubcategoryName, setNewSubcategoryName] = useState('');
  
  // Single and Bulk Delete States
  const [singleDeleteExpense, setSingleDeleteExpense] = useState(null);
  const [selectedIds, setSelectedIds] = useState([]);
  const [isBulkDeleteModalOpen, setIsBulkDeleteModalOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);



  const fetchExpenses = async () => {
    try {
      const data = await expensesService.getAll();
      const businessExpenses = (Array.isArray(data) ? data : []).filter(e => !isPersonalCategory(e.category));
      setExpenses(businessExpenses);
    } catch (err) {
      console.error('Error fetching expenses:', err);
    }
  };

  const fetchCategories = async () => {
    try {
      const data = await expensesService.getCategories();
      if (data && Object.keys(data).length > 0) {
        const businessMap = {};
        Object.entries(data).forEach(([cat, subcats]) => {
          if (!isPersonalCategory(cat)) {
            businessMap[cat] = subcats;
          }
        });
        setCategoryMap(businessMap);
      }
    } catch (err) {
      console.error('Error fetching categories:', err);
    }
  };

  useEffect(() => {
    Promise.all([
      fetchExpenses(),
      fetchCategories()
    ]);
  }, []);

  const handleOpenModal = (expense = null) => {
    setIsAddingCategory(false);
    setIsAddingSubcategory(false);
    setNewCategoryName('');
    setNewSubcategoryName('');

    if (expense) {
      setEditingExpense(expense);
      
      // Ensure the expense's category and subcategory exist in our map
      let currentMap = { ...categoryMap };
      let mapChanged = false;
      
      if (!currentMap[expense.category]) {
        currentMap[expense.category] = [];
        mapChanged = true;
      }
      if (expense.subcategory && !currentMap[expense.category].includes(expense.subcategory)) {
        currentMap[expense.category].push(expense.subcategory);
        mapChanged = true;
      }
      if (mapChanged) setCategoryMap(currentMap);

      setFormData({
        description: expense.description,
        amount: expense.amount,
        category: expense.category || Object.keys(categoryMap)[0] || 'Utilities',
        subcategory: expense.subcategory || currentMap[expense.category || Object.keys(categoryMap)[0] || 'Utilities']?.[0] || '',
        quantity: expense.quantity || 1,
        metric: expense.metric || 'units',
        expense_date: formatDateLocal(expense.expense_date)
      });
    } else {
      setEditingExpense(null);
      const d = new Date();
      const today = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
      const firstCat = Object.keys(categoryMap)[0] || 'Utilities';
      const firstSubcat = categoryMap[firstCat]?.[0] || 'Electricity';
      setFormData({ description: '', amount: '', category: firstCat, subcategory: firstSubcat, quantity: 1, metric: 'units', expense_date: today });
    }
    setIsModalOpen(true);
  };

  const handleCategoryChange = (e) => {
    const val = e.target.value;
    if (val === '__ADD_NEW__') {
      setIsAddingCategory(true);
      setFormData({ ...formData, category: '', subcategory: '' });
    } else {
      setIsAddingCategory(false);
      setFormData({
        ...formData,
        category: val,
        subcategory: categoryMap[val]?.[0] || ''
      });
    }
  };

  const handleSubcategoryChange = (e) => {
    const val = e.target.value;
    if (val === '__ADD_NEW__') {
      setIsAddingSubcategory(true);
      setFormData({ ...formData, subcategory: '' });
    } else {
      setIsAddingSubcategory(false);
      setFormData({ ...formData, subcategory: val });
    }
  };

  const confirmNewCategory = async () => {
    const catName = newCategoryName.trim();
    if (catName) {
      if (isPersonalCategory(catName)) {
        alert(`"${catName}" is a Personal Expense category. Please manage personal expenses on the Personal Expenses page.`);
        return;
      }
      const subcategories = ['General'];
      const updatedMap = { ...categoryMap, [catName]: subcategories };
      setCategoryMap(updatedMap);
      setFormData({ ...formData, category: catName, subcategory: 'General' });
      setIsAddingCategory(false);
      setNewCategoryName('');
      
      try {
        await expensesService.saveCategory(catName, subcategories);
      } catch (err) {
        console.error('Error saving category:', err);
      }
    }
  };

  const confirmNewSubcategory = async () => {
    const subcatName = newSubcategoryName.trim();
    if (subcatName) {
      const updatedMap = { ...categoryMap };
      if (!updatedMap[formData.category].includes(subcatName)) {
        updatedMap[formData.category].push(subcatName);
      }
      setCategoryMap(updatedMap);
      setFormData({ ...formData, subcategory: subcatName });
      setIsAddingSubcategory(false);
      setNewSubcategoryName('');

      try {
        await expensesService.saveCategory(formData.category, updatedMap[formData.category]);
      } catch (err) {
        console.error('Error saving subcategory:', err);
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingExpense) {
        await expensesService.update(editingExpense.id, formData);
      } else {
        await expensesService.create(formData);
      }
      setIsModalOpen(false);
      fetchExpenses();
    } catch (err) {
      console.error('Error saving expense:', err);
      alert('Error saving expense: ' + err.message);
    }
  };

  const handleSelectRow = (id) => {
    setSelectedIds(prev => 
      prev.includes(id) ? prev.filter(item => item !== id) : [...prev, id]
    );
  };

  const handleSelectAllCurrentPage = () => {
    const pageIds = paginatedExpenses.map(e => e.id);
    const isAllPageSelected = pageIds.length > 0 && pageIds.every(id => selectedIds.includes(id));
    
    if (isAllPageSelected) {
      setSelectedIds(prev => prev.filter(id => !pageIds.includes(id)));
    } else {
      setSelectedIds(prev => {
        const newSet = new Set([...prev, ...pageIds]);
        return Array.from(newSet);
      });
    }
  };

  const handleSelectAllFiltered = () => {
    const allFilteredIds = filteredExpenses.map(e => e.id);
    const isAllFilteredSelected = allFilteredIds.length > 0 && allFilteredIds.every(id => selectedIds.includes(id));
    if (isAllFilteredSelected) {
      setSelectedIds([]);
    } else {
      setSelectedIds(allFilteredIds);
    }
  };

  const handleClearSelection = () => {
    setSelectedIds([]);
  };

  const handleDelete = (expense) => {
    setSingleDeleteExpense(expense);
  };

  const confirmSingleDelete = async () => {
    if (!singleDeleteExpense) return;
    setIsDeleting(true);
    try {
      await expensesService.delete(singleDeleteExpense.id);
      setSelectedIds(prev => prev.filter(id => id !== singleDeleteExpense.id));
      setSingleDeleteExpense(null);
      await fetchExpenses();
    } catch (err) {
      console.error('Error deleting expense:', err);
      alert('Error deleting expense: ' + err.message);
    } finally {
      setIsDeleting(false);
    }
  };

  const confirmBulkDelete = async () => {
    if (selectedIds.length === 0) return;
    setIsDeleting(true);
    try {
      await expensesService.deleteMultiple(selectedIds);
      setSelectedIds([]);
      setIsBulkDeleteModalOpen(false);
      await fetchExpenses();
    } catch (err) {
      console.error('Error deleting expenses:', err);
      alert('Error deleting expenses: ' + err.message);
    } finally {
      setIsDeleting(false);
    }
  };

  const businessExpenses = expenses.filter(e => !isPersonalCategory(e.category));

  const filteredExpenses = businessExpenses.filter(e => {
    const passCategory = filterCategory === 'All' ? true : e.category === filterCategory;
    const passSubcategory = filterSubcategory === 'All' ? true : (e.subcategory || '').toLowerCase() === filterSubcategory.toLowerCase();
    
    let passStartDate = true;
    let passEndDate = true;
    let passFilterMonth = true;
    
    const expDateStr = formatDateLocal(e.expense_date);
    
    if (startDate && expDateStr) {
      passStartDate = expDateStr >= startDate;
    }
    if (endDate && expDateStr) {
      passEndDate = expDateStr <= endDate;
    }
    if (filterMonth && expDateStr) {
      passFilterMonth = expDateStr.substring(0, 7) === filterMonth;
    }
    
    return passCategory && passSubcategory && passStartDate && passEndDate && passFilterMonth;
  });

  const [currentPage, setCurrentPage] = useSessionStorage('expenses_currentPage', 1);
  const itemsPerPage = 10;

  useEffect(() => {
    setCurrentPage(1);
  }, [filterCategory, filterSubcategory, startDate, endDate, filterMonth]);

  const totalPages = Math.ceil(filteredExpenses.length / itemsPerPage) || 1;
  const paginatedExpenses = filteredExpenses.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);

  const isCurrentPageAllSelected = paginatedExpenses.length > 0 && paginatedExpenses.every(e => selectedIds.includes(e.id));
  const isCurrentPageSomeSelected = paginatedExpenses.some(e => selectedIds.includes(e.id)) && !isCurrentPageAllSelected;
  const selectedExpenses = expenses.filter(e => selectedIds.includes(e.id));
  const totalSelectedAmount = selectedExpenses.reduce((acc, curr) => acc + Number(curr.amount || 0), 0);

  const availableSubcategories = filterCategory === 'All' ? [] : (categoryMap[filterCategory] || []);

  const totalThisMonth = businessExpenses.reduce((acc, curr) => {
    const d = parseDateSafe(curr.expense_date);
    if (!d) return acc;
    const now = new Date();
    if (d.getMonth() === now.getMonth() && d.getFullYear() === now.getFullYear()) {
      return acc + Number(curr.amount);
    }
    return acc;
  }, 0);

  const totalLastMonth = businessExpenses.reduce((acc, curr) => {
    const d = parseDateSafe(curr.expense_date);
    if (!d) return acc;
    const now = new Date();
    let lastMonth = now.getMonth() - 1;
    let year = now.getFullYear();
    if (lastMonth < 0) {
      lastMonth = 11;
      year -= 1;
    }
    if (d.getMonth() === lastMonth && d.getFullYear() === year) {
      return acc + Number(curr.amount);
    }
    return acc;
  }, 0);

  const totalFilteredAmount = filteredExpenses.reduce((acc, curr) => acc + Number(curr.amount), 0);

  const thisMonthName = new Date().toLocaleString('default', { month: 'long' });
  const lastMonthDate = new Date();
  lastMonthDate.setMonth(lastMonthDate.getMonth() - 1);
  const lastMonthName = lastMonthDate.toLocaleString('default', { month: 'long' });

  const categoryData = Object.entries(
    filteredExpenses.reduce((acc, exp) => {
      const cat = exp.category || 'Other';
      acc[cat] = (acc[cat] || 0) + Number(exp.amount);
      return acc;
    }, {})
  ).map(([name, amount]) => ({ name, amount }));

  const pieData = (() => {
    const sorted = [...categoryData].sort((a, b) => b.amount - a.amount);
    if (sorted.length <= 6) return sorted;
    const top5 = sorted.slice(0, 5);
    const otherSum = sorted.slice(5).reduce((sum, item) => sum + item.amount, 0);
    return [...top5, { name: 'Other', amount: otherSum }];
  })();

  const COLORS = [
    '#3b82f6', // blue
    '#10b981', // green/emerald
    '#f59e0b', // amber
    '#ec4899', // pink
    '#8b5cf6', // purple
    '#ef4444', // red
    '#06b6d4', // cyan
    '#84cc16', // lime
    '#a855f7', // purple-light
    '#f97316'  // orange
  ];

  const handleExport = () => {
    const headers = ['Date', 'Description', 'Category', 'Subcategory', 'Quantity', 'Metric Unit', 'Amount (₹)'];
    const rows = [headers];
    filteredExpenses.forEach(exp => {
      rows.push([
        formatDateLocal(exp.expense_date),
        `"${(exp.description || '').replace(/"/g, '""')}"`,
        `"${exp.category || ''}"`,
        `"${exp.subcategory || ''}"`,
        exp.quantity || 1,
        exp.metric || 'units',
        exp.amount || 0
      ]);
    });

    const csvContent = "data:text/csv;charset=utf-8," + rows.map(e => e.join(",")).join("\n");
    const encodedUri = encodeURI(csvContent);
    const link = document.createElement("a");
    link.setAttribute("href", encodedUri);
    link.setAttribute("download", `Expenses_Export_${filterMonth || 'All'}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-8 flex-wrap gap-4">
        <div>
          <h2>Expenses Management</h2>
          <p>Track and manage all PG-related expenditures.</p>
        </div>
        <div className="flex items-center gap-4 flex-wrap">
          <input
            type="month"
            value={filterMonth || ''}
            onChange={(e) => setFilterMonth(e.target.value)}
            style={{ 
              width: '180px',
              padding: '0.5rem 1rem', 
              borderRadius: '0.5rem', 
              border: '1px solid var(--border-color)', 
              backgroundColor: 'rgba(0,0,0,0.2)', 
              color: 'var(--text-primary)',
              colorScheme: 'dark'
            }}
            title="Select month for export"
          />
          <button className="btn-primary flex items-center gap-2" onClick={handleExport} style={{ backgroundColor: 'var(--surface-highlight)', color: 'var(--text-primary)' }}>
            <Download size={16} /> Export CSV
          </button>
          <button className="btn-primary flex items-center gap-2" onClick={() => handleOpenModal()}>
            <Plus size={16} /> Record Expense
          </button>
        </div>
      </div>

      <div className="grid grid-cols-3 gap-6 mb-8">
        <div className="card">
          <div className="card-title">This Month ({thisMonthName})</div>
          <div className="card-value" style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', color: 'var(--danger-color)' }}>
            ₹{totalThisMonth.toLocaleString()}
          </div>
        </div>
        <div className="card">
          <div className="card-title">Last Month ({lastMonthName})</div>
          <div className="card-value" style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', color: 'var(--text-primary)' }}>
            ₹{totalLastMonth.toLocaleString()}
          </div>
        </div>
        <div className="card">
          <div className="card-title">Total {filterCategory === 'All' ? 'Amount (All)' : `Amount (${filterCategory}${filterSubcategory !== 'All' ? ` - ${filterSubcategory}` : ''})`}</div>
          <div className="card-value" style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', color: 'var(--text-primary)' }}>
            ₹{totalFilteredAmount.toLocaleString()}
          </div>
        </div>
      </div>

      {/* Category-wise Expenses Chart */}
      <div className="card mb-8" style={{ padding: '1.5rem' }}>
        <h3 style={{ fontSize: '1rem', marginBottom: '1.5rem', marginTop: 0, color: 'var(--text-primary)', fontWeight: '600' }}>
          Expense Breakdown Analysis
        </h3>
        {categoryData.length === 0 ? (
          <div style={{ height: '280px', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--text-secondary)' }}>
            No expense data available to display charts.
          </div>
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: '2.5rem' }}>
            {/* Bar Chart */}
            <div>
              <h4 style={{ margin: '0 0 1rem 0', color: 'var(--text-secondary)', fontSize: '0.875rem', fontWeight: '500' }}>Category-wise Amounts</h4>
              <div style={{ height: '280px', width: '100%', minWidth: 0 }}>
                <ResponsiveContainer width="100%" height="100%" minWidth={0}>
                  <BarChart data={categoryData} margin={{ top: 10, right: 10, left: 10, bottom: 5 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="var(--border-color)" vertical={false} />
                    <XAxis 
                      dataKey="name" 
                      stroke="var(--text-secondary)" 
                      tick={{ fontSize: 11 }} 
                    />
                    <YAxis 
                      stroke="var(--text-secondary)" 
                      tick={{ fontSize: 11 }} 
                      tickFormatter={(val) => `₹${val}`}
                    />
                    <RechartsTooltip 
                      contentStyle={{ backgroundColor: 'var(--surface-color)', borderColor: 'var(--border-color)', borderRadius: '0.5rem', color: '#fff', fontSize: '0.85rem' }}
                      itemStyle={{ color: '#fff' }}
                      formatter={(value) => [`₹${value.toLocaleString()}`, 'Total Spent']}
                    />
                    <Bar dataKey="amount" fill="var(--primary-color)" radius={[4, 4, 0, 0]} maxBarSize={50}>
                      {categoryData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>

            {/* Pie Chart */}
            <div>
              <h4 style={{ margin: '0 0 1rem 0', color: 'var(--text-secondary)', fontSize: '0.875rem', fontWeight: '500' }}>Expense Distribution Share</h4>
              <div style={{ height: '280px', width: '100%', minWidth: 0 }}>
                <ResponsiveContainer width="100%" height="100%" minWidth={0}>
                  <PieChart>
                    <Pie
                      data={pieData}
                      cx="50%"
                      cy="50%"
                      labelLine={true}
                      label={({ name, percent }) => `${name} (${(percent * 100).toFixed(0)}%)`}
                      outerRadius={80}
                      dataKey="amount"
                    >
                      {pieData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                      ))}
                    </Pie>
                    <RechartsTooltip 
                      contentStyle={{ backgroundColor: 'var(--surface-color)', borderColor: 'var(--border-color)', borderRadius: '0.5rem', color: '#fff', fontSize: '0.85rem' }}
                      itemStyle={{ color: '#fff' }}
                      formatter={(value) => [`₹${Number(value).toLocaleString()}`, 'Amount']}
                    />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            </div>
          </div>
        )}
      </div>

      <div className="card">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem', flexWrap: 'wrap', gap: '1rem' }}>
          <h3 style={{ margin: 0 }}>Recent Expenses ({filteredExpenses.length})</h3>
          <div style={{ display: 'flex', gap: '1rem', alignItems: 'center', flexWrap: 'wrap' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <span style={{ fontSize: '0.875rem', color: 'var(--text-secondary)' }}>From:</span>
              <input 
                type="date" 
                value={startDate} 
                onChange={e => setStartDate(e.target.value)}
                style={{ padding: '0.5rem', borderRadius: '0.5rem', border: '1px solid var(--border-color)', backgroundColor: 'rgba(0,0,0,0.2)', color: 'var(--text-primary)', fontSize: '0.875rem' }}
              />
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <span style={{ fontSize: '0.875rem', color: 'var(--text-secondary)' }}>To:</span>
              <input 
                type="date" 
                value={endDate} 
                onChange={e => setEndDate(e.target.value)}
                style={{ padding: '0.5rem', borderRadius: '0.5rem', border: '1px solid var(--border-color)', backgroundColor: 'rgba(0,0,0,0.2)', color: 'var(--text-primary)', fontSize: '0.875rem' }}
              />
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <span style={{ fontSize: '0.875rem', color: 'var(--text-secondary)' }}>Month:</span>
              <input 
                type="month" 
                value={filterMonth || ''} 
                onChange={e => setFilterMonth(e.target.value)}
                style={{ 
                  padding: '0.5rem', 
                  borderRadius: '0.5rem', 
                  border: '1px solid var(--border-color)', 
                  backgroundColor: 'rgba(0,0,0,0.2)', 
                  color: 'var(--text-primary)', 
                  fontSize: '0.875rem',
                  colorScheme: 'dark'
                }}
              />
            </div>
            <select 
              value={filterCategory}
              onChange={(e) => { setFilterCategory(e.target.value); setFilterSubcategory('All'); }}
              style={{ padding: '0.5rem 1rem', borderRadius: '0.5rem', border: '1px solid var(--border-color)', backgroundColor: 'rgba(0,0,0,0.2)', color: 'var(--text-primary)' }}
            >
              <option value="All" style={{ color: '#000' }}>All Categories</option>
              {Object.keys(categoryMap).filter(cat => !isPersonalCategory(cat)).map(cat => (
                <option key={cat} value={cat} style={{ color: '#000' }}>{cat}</option>
              ))}
            </select>

            {filterCategory !== 'All' && availableSubcategories.length > 0 && (
              <select 
                value={filterSubcategory}
                onChange={(e) => setFilterSubcategory(e.target.value)}
                style={{ padding: '0.5rem 1rem', borderRadius: '0.5rem', border: '1px solid var(--border-color)', backgroundColor: 'rgba(0,0,0,0.2)', color: 'var(--text-primary)' }}
              >
                <option value="All" style={{ color: '#000' }}>All Subcategories</option>
                {availableSubcategories.map(sub => (
                  <option key={sub} value={sub} style={{ color: '#000' }}>{sub}</option>
                ))}
              </select>
            )}
          </div>
        </div>
        {/* Bulk Action Bar */}
        {selectedIds.length > 0 && (
          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            backgroundColor: 'rgba(59, 130, 246, 0.12)',
            border: '1px solid rgba(59, 130, 246, 0.3)',
            borderRadius: '0.75rem',
            padding: '0.75rem 1.25rem',
            marginBottom: '1.25rem',
            flexWrap: 'wrap',
            gap: '1rem'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', flexWrap: 'wrap' }}>
              <span style={{ fontWeight: '600', color: 'var(--text-primary)', fontSize: '0.9rem' }}>
                {selectedIds.length} {selectedIds.length === 1 ? 'record' : 'records'} selected
              </span>
              <span style={{ color: 'var(--text-secondary)', fontSize: '0.85rem' }}>
                Total: <strong style={{ color: 'var(--danger-color)' }}>₹{totalSelectedAmount.toLocaleString()}</strong>
              </span>
              {filteredExpenses.length > paginatedExpenses.length && (
                <button 
                  type="button"
                  onClick={handleSelectAllFiltered}
                  style={{
                    background: 'none',
                    border: 'none',
                    color: 'var(--primary-color)',
                    fontSize: '0.85rem',
                    cursor: 'pointer',
                    textDecoration: 'underline',
                    padding: 0,
                    fontWeight: '500'
                  }}
                >
                  {selectedIds.length === filteredExpenses.length ? 'Select page only' : `Select all ${filteredExpenses.length} filtered records`}
                </button>
              )}
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
              <button 
                type="button"
                onClick={handleClearSelection}
                style={{
                  padding: '0.45rem 0.9rem',
                  borderRadius: '0.375rem',
                  border: '1px solid var(--border-color)',
                  backgroundColor: 'rgba(255,255,255,0.05)',
                  color: 'var(--text-secondary)',
                  cursor: 'pointer',
                  fontSize: '0.85rem',
                  fontWeight: '500'
                }}
              >
                Clear Selection
              </button>
              <button 
                type="button"
                onClick={() => setIsBulkDeleteModalOpen(true)}
                style={{
                  padding: '0.45rem 1rem',
                  borderRadius: '0.375rem',
                  border: 'none',
                  backgroundColor: 'var(--danger-color)',
                  color: '#fff',
                  cursor: 'pointer',
                  fontSize: '0.85rem',
                  fontWeight: '600',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.4rem'
                }}
              >
                <Trash2 size={15} /> Delete Selected ({selectedIds.length})
              </button>
            </div>
          </div>
        )}

        <div className="table-container" style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left' }}>
            <thead>
              <tr style={{ borderBottom: '1px solid var(--border-color)', color: 'var(--text-secondary)' }}>
                <th style={{ padding: '1rem 0.75rem', width: '40px', textAlign: 'center' }}>
                  <input 
                    type="checkbox"
                    checked={isCurrentPageAllSelected}
                    ref={input => {
                      if (input) {
                        input.indeterminate = isCurrentPageSomeSelected;
                      }
                    }}
                    onChange={handleSelectAllCurrentPage}
                    style={{ width: '1.15rem', height: '1.15rem', cursor: 'pointer', accentColor: 'var(--primary-color)' }}
                    title="Select all records on current page"
                  />
                </th>
                <th style={{ padding: '1rem 0', fontWeight: '500', minWidth: '130px' }}>Date</th>
                <th style={{ padding: '1rem 0', fontWeight: '500', minWidth: '150px' }}>Description</th>
                <th style={{ padding: '1rem 0', fontWeight: '500' }}>Category & Subcategory</th>
                <th style={{ padding: '1rem 0', fontWeight: '500' }}>Quantity</th>
                <th style={{ padding: '1rem 0', fontWeight: '500' }}>Amount (₹)</th>
                <th style={{ padding: '1rem 0', fontWeight: '500', textAlign: 'right', minWidth: '100px' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredExpenses.length === 0 ? (
                <tr>
                  <td colSpan="7" style={{ padding: '2rem', textAlign: 'center', color: 'var(--text-secondary)' }}>
                    No expenses found for this category.
                  </td>
                </tr>
              ) : paginatedExpenses.map((expense) => (
                <tr 
                  key={expense.id} 
                  style={{ 
                    borderBottom: '1px solid var(--border-color)',
                    backgroundColor: selectedIds.includes(expense.id) ? 'rgba(59, 130, 246, 0.08)' : 'transparent',
                    transition: 'background-color 0.15s ease'
                  }}
                >
                  <td style={{ padding: '1rem 0.75rem', textAlign: 'center' }}>
                    <input 
                      type="checkbox"
                      checked={selectedIds.includes(expense.id)}
                      onChange={() => handleSelectRow(expense.id)}
                      style={{ width: '1.15rem', height: '1.15rem', cursor: 'pointer', accentColor: 'var(--primary-color)' }}
                      title="Select this expense record"
                    />
                  </td>
                  <td style={{ padding: '1rem 0', color: 'var(--text-primary)', whiteSpace: 'nowrap' }}>
                    {expense.expense_date ? (() => {
                      const d = parseDateSafe(expense.expense_date);
                      return d ? (
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                          <div style={{ 
                            width: '42px', height: '42px', 
                            minWidth: '42px', flexShrink: 0,
                            borderRadius: '8px', 
                            backgroundColor: 'rgba(59, 130, 246, 0.1)',
                            color: 'var(--primary-color)',
                            display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center'
                          }}>
                            <span style={{ fontSize: '0.65rem', fontWeight: '700', textTransform: 'uppercase', lineHeight: '1' }}>
                              {getMonthShortName(expense.expense_date)}
                            </span>
                            <span style={{ fontSize: '1.1rem', fontWeight: '700', lineHeight: '1', marginTop: '2px' }}>
                              {d.getDate()}
                            </span>
                          </div>
                          <span style={{ fontSize: '0.875rem', color: 'var(--text-secondary)', fontWeight: '500' }}>
                            {d.getFullYear()}
                          </span>
                        </div>
                      ) : '-';
                    })() : '-'}
                  </td>
                  <td style={{ padding: '1rem 0', fontWeight: '500', color: 'var(--text-primary)' }}>
                    {expense.description}
                  </td>
                  <td style={{ padding: '1rem 0' }}>
                    <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                      <span style={{ 
                        padding: '0.25rem 0.75rem', 
                        borderRadius: '9999px', 
                        backgroundColor: 'rgba(255,255,255,0.05)',
                        fontSize: '0.75rem',
                        color: 'var(--text-primary)'
                      }}>{expense.category}</span>
                      {expense.subcategory && (
                        <span style={{ 
                          padding: '0.25rem 0.75rem', 
                          borderRadius: '9999px', 
                          backgroundColor: 'rgba(16, 185, 129, 0.1)',
                          color: 'var(--accent-color)',
                          fontSize: '0.75rem'
                        }}>{expense.subcategory}</span>
                      )}
                    </div>
                  </td>
                  <td style={{ padding: '1rem 0', color: 'var(--text-secondary)' }}>
                    {expense.quantity} <span style={{ fontSize: '0.75rem' }}>{expense.metric}</span>
                  </td>
                  <td style={{ padding: '1rem 0', fontWeight: '600', color: 'var(--danger-color)' }}>
                    -₹{expense.amount}
                  </td>
                  <td style={{ padding: '1rem 0', textAlign: 'right' }}>
                    <div className="flex justify-end gap-2">
                      <button onClick={() => handleOpenModal(expense)} style={{ padding: '0.5rem', background: 'transparent', color: 'var(--text-secondary)' }} title="Edit expense">
                        <Edit2 size={16} />
                      </button>
                      <button onClick={() => handleDelete(expense)} style={{ padding: '0.5rem', background: 'transparent', color: 'var(--danger-color)' }} title="Delete expense">
                        <Trash2 size={16} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {totalPages > 1 && (
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '1rem 1.5rem', borderTop: '1px solid var(--border-color)' }}>
            <span style={{ fontSize: '0.875rem', color: 'var(--text-secondary)' }}>
              Showing {(currentPage - 1) * itemsPerPage + 1} to {Math.min(currentPage * itemsPerPage, filteredExpenses.length)} of {filteredExpenses.length} entries
            </span>
            <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
              <button 
                onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                disabled={currentPage === 1}
                style={{ padding: '0.5rem 1rem', borderRadius: '0.5rem', border: '1px solid var(--border-color)', background: 'transparent', cursor: currentPage === 1 ? 'not-allowed' : 'pointer', color: 'var(--text-primary)', opacity: currentPage === 1 ? 0.5 : 1 }}
              >
                Previous
              </button>
              <span style={{ fontSize: '0.875rem', fontWeight: '500' }}>
                Page {currentPage} of {totalPages}
              </span>
              <button 
                onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                disabled={currentPage === totalPages}
                style={{ padding: '0.5rem 1rem', borderRadius: '0.5rem', border: '1px solid var(--border-color)', background: 'transparent', cursor: currentPage === totalPages ? 'not-allowed' : 'pointer', color: 'var(--text-primary)', opacity: currentPage === totalPages ? 0.5 : 1 }}
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>

      <Modal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} title={editingExpense ? "Edit Expense" : "Record Expense"}>
        <form onSubmit={handleSubmit} className="flex flex-col gap-4">
          <div>
            <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.875rem' }}>Description</label>
            <input required type="text" value={formData.description} onChange={e => setFormData({...formData, description: e.target.value})} placeholder="e.g. Electricity Bill" />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.875rem' }}>Amount (₹)</label>
              <input required type="number" value={formData.amount} onChange={e => setFormData({...formData, amount: e.target.value})} placeholder="e.g. 1500" />
            </div>
            <div>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.875rem' }}>Date</label>
              <input required type="date" value={formData.expense_date} onChange={e => setFormData({...formData, expense_date: e.target.value})} />
            </div>
          </div>
          
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.875rem' }}>Quantity</label>
              <input required type="number" step="0.01" value={formData.quantity} onChange={e => setFormData({...formData, quantity: e.target.value})} placeholder="e.g. 5" />
            </div>
            <div>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.875rem' }}>Metric Unit</label>
              <select value={formData.metric} onChange={e => setFormData({...formData, metric: e.target.value})} style={{ width: '100%', padding: '0.75rem 1rem', borderRadius: '0.5rem', border: '1px solid var(--border-color)', backgroundColor: 'rgba(0,0,0,0.2)', color: 'var(--text-primary)' }}>
                <option value="units" style={{ color: '#000' }}>Units / Pieces</option>
                <option value="kg" style={{ color: '#000' }}>Kilograms (kg)</option>
                <option value="liters" style={{ color: '#000' }}>Liters (L)</option>
                <option value="months" style={{ color: '#000' }}>Months</option>
                <option value="days" style={{ color: '#000' }}>Days</option>
                <option value="kw/h" style={{ color: '#000' }}>kW/h (Electricity)</option>
                <option value="boxes" style={{ color: '#000' }}>Boxes</option>
                <option value="meters" style={{ color: '#000' }}>Meters</option>
                <option value="gallons" style={{ color: '#000' }}>Gallons</option>
                <option value="services" style={{ color: '#000' }}>Services / Visits</option>
              </select>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            {/* Category Field */}
            <div>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.875rem' }}>Category</label>
              {!isAddingCategory ? (
                <select 
                  value={formData.category} 
                  onChange={handleCategoryChange}
                  style={{ width: '100%', padding: '0.75rem 1rem', borderRadius: '0.5rem', border: '1px solid var(--border-color)', backgroundColor: 'rgba(0,0,0,0.2)', color: 'var(--text-primary)' }}
                >
                  {Object.keys(categoryMap).map(cat => (
                    <option key={cat} value={cat} style={{ color: '#000' }}>{cat}</option>
                  ))}
                  <option value="__ADD_NEW__" style={{ color: '#000', fontWeight: 'bold' }}>+ Add New Category</option>
                </select>
              ) : (
                <div className="flex gap-2">
                  <input autoFocus type="text" value={newCategoryName} onChange={e => setNewCategoryName(e.target.value)} placeholder="New category..." />
                  <button type="button" onClick={confirmNewCategory} style={{ padding: '0 1rem', borderRadius: '0.5rem', backgroundColor: 'var(--primary-color)', color: '#fff', border: 'none' }}>Add</button>
                  <button type="button" onClick={() => setIsAddingCategory(false)} style={{ padding: '0 1rem', borderRadius: '0.5rem', backgroundColor: 'var(--surface-highlight)', color: '#fff', border: 'none' }}>X</button>
                </div>
              )}
            </div>

            {/* Subcategory Field */}
            <div>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.875rem' }}>Subcategory</label>
              {!isAddingSubcategory ? (
                <select 
                  disabled={isAddingCategory || !formData.category}
                  value={formData.subcategory} 
                  onChange={handleSubcategoryChange}
                  style={{ width: '100%', padding: '0.75rem 1rem', borderRadius: '0.5rem', border: '1px solid var(--border-color)', backgroundColor: 'rgba(0,0,0,0.2)', color: 'var(--text-primary)' }}
                >
                  {formData.category && categoryMap[formData.category] ? (
                    categoryMap[formData.category].map(subcat => (
                      <option key={subcat} value={subcat} style={{ color: '#000' }}>{subcat}</option>
                    ))
                  ) : (
                    <option value="" style={{ color: '#000' }}>Select Category First</option>
                  )}
                  {formData.category && <option value="__ADD_NEW__" style={{ color: '#000', fontWeight: 'bold' }}>+ Add New Subcategory</option>}
                </select>
              ) : (
                <div className="flex gap-2">
                  <input autoFocus type="text" value={newSubcategoryName} onChange={e => setNewSubcategoryName(e.target.value)} placeholder="New subcategory..." />
                  <button type="button" onClick={confirmNewSubcategory} style={{ padding: '0 1rem', borderRadius: '0.5rem', backgroundColor: 'var(--primary-color)', color: '#fff', border: 'none' }}>Add</button>
                  <button type="button" onClick={() => setIsAddingSubcategory(false)} style={{ padding: '0 1rem', borderRadius: '0.5rem', backgroundColor: 'var(--surface-highlight)', color: '#fff', border: 'none' }}>X</button>
                </div>
              )}
            </div>
          </div>
          <button type="submit" className="btn-primary mt-4">
            {editingExpense ? 'Update Expense' : 'Save Expense'}
          </button>
        </form>
      </Modal>

      {/* Single Expense Delete Confirmation Modal */}
      <Modal isOpen={!!singleDeleteExpense} onClose={() => !isDeleting && setSingleDeleteExpense(null)} title="Delete Expense">
        <div style={{ display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>
          <p style={{ margin: 0, color: 'var(--text-secondary)', fontSize: '0.95rem', lineHeight: '1.5' }}>
            Are you sure you want to delete this expense record? This action is permanent and cannot be undone.
          </p>
          {singleDeleteExpense && (
            <div style={{ 
              padding: '0.875rem 1rem', 
              borderRadius: '0.5rem', 
              backgroundColor: 'rgba(255,255,255,0.03)', 
              border: '1px solid var(--border-color)',
              fontSize: '0.875rem',
              display: 'flex',
              flexDirection: 'column',
              gap: '0.35rem'
            }}>
              <div style={{ color: 'var(--text-primary)', fontWeight: '600', fontSize: '0.95rem' }}>
                {singleDeleteExpense.description}
              </div>
              <div style={{ color: 'var(--text-secondary)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span>{singleDeleteExpense.category} {singleDeleteExpense.subcategory ? `• ${singleDeleteExpense.subcategory}` : ''}</span>
                <span style={{ color: 'var(--danger-color)', fontWeight: '700' }}>-₹{Number(singleDeleteExpense.amount).toLocaleString()}</span>
              </div>
              <div style={{ color: 'var(--text-secondary)', fontSize: '0.8rem' }}>
                Date: {formatDateLocal(singleDeleteExpense.expense_date)}
              </div>
            </div>
          )}
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.75rem', marginTop: '0.5rem' }}>
            <button 
              type="button"
              disabled={isDeleting}
              onClick={() => setSingleDeleteExpense(null)} 
              style={{ 
                padding: '0.75rem 1.5rem', 
                borderRadius: '0.5rem', 
                border: '1px solid var(--border-color)', 
                background: 'rgba(255,255,255,0.05)', 
                color: 'var(--text-primary)', 
                cursor: isDeleting ? 'not-allowed' : 'pointer', 
                fontSize: '0.875rem', 
                fontWeight: '500' 
              }}
            >
              Cancel
            </button>
            <button 
              type="button"
              disabled={isDeleting}
              onClick={confirmSingleDelete} 
              style={{ 
                padding: '0.75rem 1.5rem', 
                borderRadius: '0.5rem', 
                border: 'none', 
                backgroundColor: 'var(--danger-color)', 
                color: '#fff', 
                cursor: isDeleting ? 'not-allowed' : 'pointer', 
                fontSize: '0.875rem', 
                fontWeight: '600',
                opacity: isDeleting ? 0.7 : 1
              }}
            >
              {isDeleting ? 'Deleting...' : 'Delete'}
            </button>
          </div>
        </div>
      </Modal>

      {/* Bulk Delete Confirmation Modal */}
      <Modal isOpen={isBulkDeleteModalOpen} onClose={() => !isDeleting && setIsBulkDeleteModalOpen(false)} title="Delete Selected Expenses">
        <div style={{ display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>
          <p style={{ margin: 0, color: 'var(--text-secondary)', fontSize: '0.95rem', lineHeight: '1.5' }}>
            Are you sure you want to permanently delete the <strong style={{ color: 'var(--text-primary)' }}>{selectedIds.length}</strong> selected expense {selectedIds.length === 1 ? 'record' : 'records'}?
          </p>
          <div style={{ 
            padding: '1rem 1.25rem', 
            borderRadius: '0.5rem', 
            backgroundColor: 'rgba(239, 68, 68, 0.08)', 
            border: '1px solid rgba(239, 68, 68, 0.25)',
            fontSize: '0.875rem',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center'
          }}>
            <span style={{ color: 'var(--text-primary)', fontWeight: '500' }}>Total Amount to Delete:</span>
            <span style={{ color: 'var(--danger-color)', fontWeight: '700', fontSize: '1.15rem' }}>₹{totalSelectedAmount.toLocaleString()}</span>
          </div>
          <p style={{ margin: 0, color: 'var(--text-secondary)', fontSize: '0.85rem' }}>
            This action is permanent and cannot be undone.
          </p>
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.75rem', marginTop: '0.5rem' }}>
            <button 
              type="button"
              disabled={isDeleting}
              onClick={() => setIsBulkDeleteModalOpen(false)} 
              style={{ 
                padding: '0.75rem 1.5rem', 
                borderRadius: '0.5rem', 
                border: '1px solid var(--border-color)', 
                background: 'rgba(255,255,255,0.05)', 
                color: 'var(--text-primary)', 
                cursor: isDeleting ? 'not-allowed' : 'pointer', 
                fontSize: '0.875rem', 
                fontWeight: '500' 
              }}
            >
              Cancel
            </button>
            <button 
              type="button"
              disabled={isDeleting}
              onClick={confirmBulkDelete} 
              style={{ 
                padding: '0.75rem 1.5rem', 
                borderRadius: '0.5rem', 
                border: 'none', 
                backgroundColor: 'var(--danger-color)', 
                color: '#fff', 
                cursor: isDeleting ? 'not-allowed' : 'pointer', 
                fontSize: '0.875rem', 
                fontWeight: '600',
                opacity: isDeleting ? 0.7 : 1
              }}
            >
              {isDeleting ? `Deleting ${selectedIds.length} records...` : `Delete ${selectedIds.length} Records`}
            </button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default Expenses;
